import { useState, useCallback } from 'react'
import {
  createLocalPlayer, createLocalToken,
  setStoredPin, verifyStoredPin,
  hasStoredPin, getStoredPlayer, getStoredToken, isTokenExpired,
  setStoredToken, setStoredPlayer,
} from '../store/auth'
import type { FactionId, Player } from '../types'

type Step = 'unlock' | 'identity' | 'faction' | 'login'

interface IdentityForm {
  username: string
  email: string
  pin: string
  pinConfirm: string
}

interface LoginForm {
  username: string
  email: string
  pin: string
}

const FACTIONS: {
  id: FactionId
  name: string
  tagline: string
  description: string
  color: string
  icon: string
}[] = [
  {
    id: 'drifters',
    name: 'The Drifters',
    tagline: 'No flag. No leash.',
    description: 'Free agents who move between zones at will. Low loyalty, high survival.',
    color: '#5A7A8A',
    icon: '🌊',
  },
  {
    id: 'iron-meridian',
    name: 'Iron Meridian',
    tagline: 'Control the zones. Control the game.',
    description: 'Disciplined tacticians who dominate through order and superior creature training.',
    color: '#B8EEFF',
    icon: '⚙️',
  },
  {
    id: 'tidecallers',
    name: 'Tidecallers',
    tagline: 'Water flows through everything.',
    description: 'Masters of the Glassdeep who bond with aquatic creatures and read currents others can\'t.',
    color: '#00F0FF',
    icon: '🌊',
  },
  {
    id: 'wrecked',
    name: 'The Wrecked',
    tagline: 'Chaos is just power with no manners.',
    description: 'Outcasts and exiles who turned ruin into raw strength. Unpredictable and dangerous.',
    color: '#FF4800',
    icon: '💀',
  },
  {
    id: 'sovereign-few',
    name: 'Sovereign Few',
    tagline: 'Every bond calculated. Every move final.',
    description: 'The elite. They don\'t fight for glory — they fight to own the Atlas.',
    color: '#00F0FF',
    icon: '👑',
  },
]

export default function Login({ onAuth }: { onAuth: (player: Player, token: string) => void }) {
  // Mode: 'login' or 'register'
  const [mode, setMode] = useState<'login' | 'register'>('login')
  
  // If returning user has stored player + PIN, show unlock screen
  const isReturning = (() => {
    const p = getStoredPlayer()
    const t = getStoredToken()
    return !!(p && t && !isTokenExpired(t) && hasStoredPin())
  })()

  const [step, setStep] = useState<Step>(isReturning ? 'unlock' : mode === 'login' ? 'login' : 'identity')
  const [form, setForm] = useState<IdentityForm>({ username: '', email: '', pin: '', pinConfirm: '' })
  const [loginForm, setLoginForm] = useState<LoginForm>({ username: '', email: '', pin: '' })
  const [selectedFaction, setSelectedFaction] = useState<FactionId | null>(null)
  const [errors, setErrors] = useState<Partial<IdentityForm>>({})
  const [loginErrors, setLoginErrors] = useState<Partial<LoginForm>>({})
  const [unlockPin, setUnlockPin] = useState('')
  const [unlockError, setUnlockError] = useState('')

  function validateIdentity(): boolean {
    const next: Partial<IdentityForm> = {}
    if (!form.username.trim() || form.username.trim().length < 3) {
      next.username = 'At least 3 characters'
    } else if (!/^[a-zA-Z0-9_-]+$/.test(form.username.trim())) {
      next.username = 'Letters, numbers, _ and - only'
    }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = 'Enter a valid email or leave blank'
    }
    if (!form.pin || form.pin.length < 4) {
      next.pin = '4–6 digits required'
    } else if (form.pin !== form.pinConfirm) {
      next.pinConfirm = 'PINs do not match'
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  function handleIdentityNext() {
    if (validateIdentity()) setStep('faction')
  }

  function handleLogin() {
    const next: Partial<LoginForm> = {}
    if (!loginForm.username.trim() || loginForm.username.trim().length < 3) {
      next.username = 'At least 3 characters'
    }
    if (loginForm.pin.length < 4) {
      next.pin = '4–6 digits required'
    }
    setLoginErrors(next)
    if (Object.keys(next).length === 0) {
      // For now, create local player on login too (simplified)
      const player = createLocalPlayer(loginForm.username.trim(), loginForm.email.trim(), 'drifters')
      const token = createLocalToken(player.id, player.username)
      setStoredPin(loginForm.pin)
      setStoredToken(token)
      setStoredPlayer(player)
      onAuth(player, token)
    }
  }

  function handleModeChange(newMode: 'login' | 'register') {
    setMode(newMode)
    setStep(newMode === 'login' ? 'login' : 'identity')
    setErrors({})
    setLoginErrors({})
  }

  function handleComplete() {
    if (!selectedFaction) return
    const player = createLocalPlayer(form.username.trim(), form.email.trim(), selectedFaction)
    const token = createLocalToken(player.id, player.username)
    setStoredPin(form.pin)
    setStoredToken(token)
    setStoredPlayer(player)
    onAuth(player, token)
  }

  function handleUnlock() {
    if (verifyStoredPin(unlockPin)) {
      const player = getStoredPlayer()!
      const token = getStoredToken()!
      onAuth(player, token)
    } else {
      setUnlockError('Incorrect PIN')
      setUnlockPin('')
    }
  }

  const API_BASE = import.meta.env.DEV ? '/api' : (import.meta.env.VITE_API_URL ?? '')
  const [devLoading, setDevLoading] = useState(false)
  const [superDevLoading, setSuperDevLoading] = useState(false)

  const handleDevLogin = useCallback(async () => {
    setDevLoading(true)
    try {
      const res = await fetch(`${API_BASE}/auth/dev-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      })
      console.log('Dev login response:', res.status, res.statusText)
      const data = await res.json()
      console.log('Dev login data:', data)
      if (data.success && data.data?.token && data.data?.player) {
        console.log('Calling onAuth with:', data.data.player, data.data.token)
        onAuth(data.data.player, data.data.token)
      } else {
        console.error('Dev login failed:', data.error)
        alert('Dev login failed: ' + (data.error ?? 'unknown error'))
      }
    } catch (e) {
      console.error('Dev login error:', e)
      alert('Dev login error: ' + String(e))
    } finally {
      setDevLoading(false)
    }
  }, [API_BASE, onAuth])

  const handleSuperDevLogin = useCallback(async () => {
    setSuperDevLoading(true)
    try {
      const res = await fetch(`${API_BASE}/auth/super-dev-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      })
      console.log('Super dev login response:', res.status, res.statusText)
      const data = await res.json()
      console.log('Super dev login data:', data)
      if (data.success && data.data?.token && data.data?.player) {
        console.log('Calling onAuth with:', data.data.player, data.data.token)
        onAuth(data.data.player, data.data.token)
      } else {
        console.error('Super dev login failed:', data.error)
        alert('Super dev login failed: ' + (data.error ?? 'unknown error'))
      }
    } catch (e) {
      console.error('Super dev login error:', e)
      alert('Super dev login error: ' + String(e))
    } finally {
      setSuperDevLoading(false)
    }
  }, [API_BASE, onAuth])

  return (
    <div className="min-h-screen bg-ab-deep flex flex-col items-center justify-center px-4 relative overflow-hidden">
      {/* Atmosphere glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute w-[500px] h-[500px] rounded-full blur-[120px]"
             style={{ background: '#FF480018', top: '-10%', left: '-15%' }} />
        <div className="absolute w-80 h-80 rounded-full blur-[100px]"
             style={{ background: '#00F0FF12', bottom: '15%', right: '-10%' }} />
        <div className="absolute w-64 h-32 rounded-full blur-[80px]"
             style={{ background: '#BF00FF0A', bottom: '5%', left: '20%' }} />
        {/* Grid lines */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(0,240,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,240,255,0.03) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }} />
      </div>

      <div className="relative z-10 w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-ab mb-4"
               style={{ background: 'rgba(0,240,255,0.08)', border: '1px solid rgba(0,240,255,0.2)' }}>
            <span className="text-3xl">⚡</span>
          </div>
          <h1
            className="font-display font-black text-5xl tracking-widest uppercase mb-2"
            style={{ color: '#00F0FF', textShadow: '0 0 30px rgba(0,240,255,0.4), 0 0 60px rgba(0,240,255,0.15)' }}
          >
            Atlas Beasts
          </h1>
          <p className="text-body text-sm italic opacity-70">
            The struggle is beautiful. The bond is everything.
          </p>
        </div>

        {/* Founding member badge */}
        {step === 'identity' && (
          <div
            className="flex items-center gap-2 px-4 py-2.5 rounded-ab mb-5 border"
            style={{ background: 'rgba(0,240,255,0.04)', borderColor: 'rgba(0,240,255,0.15)' }}
          >
            <span className="text-base flex-shrink-0">🌟</span>
            <div>
              <p className="font-display font-bold text-xs text-ab-gold">Founding Member Access</p>
              <p className="text-body text-[11px] opacity-60">Pre-launch beta · All ORIGIN creatures preserved at launch</p>
            </div>
          </div>
        )}

        {/* Mode Toggle (Login vs Register) */}
        {!isReturning && (
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => handleModeChange('login')}
              className="flex-1 py-2.5 px-4 rounded-lg font-bold text-sm uppercase tracking-wider transition-all"
              style={{
                background: mode === 'login' ? 'rgba(0,240,255,0.15)' : 'rgba(58,58,90,0.3)',
                color: mode === 'login' ? '#00F0FF' : '#8A8AA5',
                border: mode === 'login' ? '1px solid rgba(0,240,255,0.4)' : '1px solid rgba(58,58,90,0.5)',
                fontFamily: 'Rajdhani, system-ui',
              }}
            >
              Login
            </button>
            <button
              onClick={() => handleModeChange('register')}
              className="flex-1 py-2.5 px-4 rounded-lg font-bold text-sm uppercase tracking-wider transition-all"
              style={{
                background: mode === 'register' ? 'rgba(0,240,255,0.15)' : 'rgba(58,58,90,0.3)',
                color: mode === 'register' ? '#00F0FF' : '#8A8AA5',
                border: mode === 'register' ? '1px solid rgba(0,240,255,0.4)' : '1px solid rgba(58,58,90,0.5)',
                fontFamily: 'Rajdhani, system-ui',
              }}
            >
              Register
            </button>
          </div>
        )}

        {/* DEV-only quick login */}
        {(step === 'identity' || step === 'login') && (
          <div className="space-y-2 mb-4">
            <button
              onClick={handleDevLogin}
              disabled={devLoading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-ab border font-display font-bold text-sm cursor-pointer transition-all"
              style={{ borderColor: 'rgba(255,232,0,0.4)', background: 'rgba(255,232,0,0.06)', color: '#FFE800', opacity: devLoading ? 0.6 : 1 }}
            >
              <span>🔧</span>
              {devLoading ? 'Logging in...' : 'Dev Login (local only)'}
            </button>
            <button
              onClick={handleSuperDevLogin}
              disabled={superDevLoading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-ab border font-display font-bold text-sm cursor-pointer transition-all"
              style={{ borderColor: 'rgba(191,0,255,0.4)', background: 'rgba(191,0,255,0.06)', color: '#BF00FF', opacity: superDevLoading ? 0.6 : 1 }}
            >
              <span>⚡</span>
              {superDevLoading ? 'Unlocking everything...' : 'SUPER Dev Login (Everything Unlocked)'}
            </button>
            {/* Quick fill buttons for dev accounts */}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setLoginForm({ username: 'dev1', email: 'dev1@test.com', pin: '1234' })
                  setMode('login')
                  setStep('login')
                }}
                className="flex-1 py-2 px-3 rounded-lg text-xs font-bold cursor-pointer transition-all"
                style={{ background: 'rgba(0,240,255,0.1)', color: '#00F0FF', border: '1px solid rgba(0,240,255,0.3)' }}
              >
                Dev 1
              </button>
              <button
                onClick={() => {
                  setLoginForm({ username: 'dev2', email: 'dev2@test.com', pin: '5678' })
                  setMode('login')
                  setStep('login')
                }}
                className="flex-1 py-2 px-3 rounded-lg text-xs font-bold cursor-pointer transition-all"
                style={{ background: 'rgba(0,240,255,0.1)', color: '#00F0FF', border: '1px solid rgba(0,240,255,0.3)' }}
              >
                Dev 2
              </button>
              <button
                onClick={() => {
                  setLoginForm({ username: '', email: '', pin: '' })
                }}
                className="flex-1 py-2 px-3 rounded-lg text-xs font-bold cursor-pointer transition-all"
                style={{ background: 'rgba(255,72,0,0.1)', color: '#FF4800', border: '1px solid rgba(255,72,0,0.3)' }}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* OAuth buttons disabled — using dev login / local profile only */}
        {/*
        {step === 'identity' && (
          <div className="flex flex-col gap-2 mb-5">
            <button onClick={() => handleOAuth('google')} ... >...</button>
            <button onClick={() => handleOAuth('discord')} ... >...</button>
            <div className="flex items-center gap-3 my-1">...</div>
          </div>
        )}
        */}

        {step === 'unlock' && (
          <UnlockStep
            pin={unlockPin}
            error={unlockError}
            onChange={val => { setUnlockPin(val); setUnlockError('') }}
            onUnlock={handleUnlock}
          />
        )}

        {step === 'login' && (
          <LoginStep
            form={loginForm}
            errors={loginErrors}
            onChange={(field, val) => {
              setLoginForm(f => ({ ...f, [field]: val }))
              if (loginErrors[field as keyof LoginForm]) setLoginErrors(e => ({ ...e, [field]: undefined }))
            }}
            onLogin={handleLogin}
          />
        )}

        {step === 'identity' && (
          <IdentityStep
            form={form}
            errors={errors}
            onChange={(field, val) => {
              setForm(f => ({ ...f, [field]: val }))
              if (errors[field as keyof IdentityForm]) setErrors(e => ({ ...e, [field]: undefined }))
            }}
            onNext={handleIdentityNext}
          />
        )}

        {step === 'faction' && (
          <FactionStep
            selected={selectedFaction}
            onSelect={setSelectedFaction}
            onBack={() => setStep('identity')}
            onComplete={handleComplete}
          />
        )}

        {/* Footer */}
        <p className="text-center text-label text-[11px] mt-6 opacity-40">
          Atlas Beasts Beta · Ploonu LLC · Pre-launch
        </p>
      </div>
    </div>
  )
}

// ── Step 1: Identity ──
function IdentityStep({
  form,
  errors,
  onChange,
  onNext,
}: {
  form: IdentityForm
  errors: Partial<IdentityForm>
  onChange: (field: keyof IdentityForm, val: string) => void
  onNext: () => void
}) {
  return (
    <div className="card-gold p-8">
      <div className="text-center mb-8">
        <p className="text-label mb-1">Step 1 of 2</p>
        <h2 className="font-display font-black text-xl text-ab-text tracking-tight mb-3">
          Who are you?
        </h2>
        <div className="divider-gold" />
      </div>

      <div className="flex flex-col gap-5">
        <div>
          <label className="text-label block mb-2">Username</label>
          <input
            className="input-base"
            placeholder="your_handle"
            name="username"
            autoComplete="username"
            value={form.username}
            onChange={e => onChange('username', e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onNext()}
            autoFocus
            maxLength={24}
          />
          {errors.username && (
            <p className="text-ab-fire text-xs mt-1 font-display font-bold">{errors.username}</p>
          )}
        </div>

        <div>
          <label className="text-label block mb-2">Email <span className="opacity-50">(optional)</span></label>
          <input
            className="input-base"
            type="email"
            placeholder="you@example.com (optional)"
            name="email"
            autoComplete="email"
            value={form.email}
            onChange={e => onChange('email', e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onNext()}
          />
          {errors.email && (
            <p className="text-ab-fire text-xs mt-1 font-display font-bold">{errors.email}</p>
          )}
        </div>

        <div>
          <label className="text-label block mb-2">PIN <span className="opacity-70 font-normal">(4–6 digits)</span></label>
          <input
            className="input-base"
            type="password"
            inputMode="numeric"
            placeholder="e.g. 1234"
            name="pin"
            autoComplete="off"
            value={form.pin}
            onChange={e => onChange('pin', e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={e => e.key === 'Enter' && onNext()}
            maxLength={6}
          />
          {errors.pin && (
            <p className="text-ab-fire text-xs mt-1 font-display font-bold">{errors.pin}</p>
          )}
        </div>

        <div>
          <label className="text-label block mb-2">Confirm PIN</label>
          <input
            className="input-base"
            type="password"
            inputMode="numeric"
            placeholder="repeat PIN"
            name="pin-confirm"
            autoComplete="off"
            value={form.pinConfirm}
            onChange={e => onChange('pinConfirm', e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={e => e.key === 'Enter' && onNext()}
            maxLength={6}
          />
          {errors.pinConfirm && (
            <p className="text-ab-fire text-xs mt-1 font-display font-bold">{errors.pinConfirm}</p>
          )}
        </div>

        <button onClick={onNext} className="btn-primary w-full mt-2">
          Continue →
        </button>
      </div>

      <div className="divider-gold mt-8 mb-5" />
      <p className="text-body text-xs text-center opacity-70">
        Your ID is generated locally. PIN protects your profile on this device.
      </p>
    </div>
  )
}

// ── Unlock: Returning user ──
function UnlockStep({
  pin,
  error,
  onChange,
  onUnlock,
}: {
  pin: string
  error: string
  onChange: (val: string) => void
  onUnlock: () => void
}) {
  const player = getStoredPlayer()
  return (
    <div className="card-gold p-8">
      <div className="text-center mb-8">
        <p className="text-label mb-1">Welcome back</p>
        <h2 className="font-display font-black text-xl text-ab-text tracking-tight mb-1">
          {player?.username ?? 'Hunter'}
        </h2>
        <p className="text-body text-xs opacity-70">Enter your PIN to continue</p>
        <div className="divider-gold mt-4" />
      </div>

      <div className="flex flex-col gap-5">
        <div>
          <label className="text-label block mb-2">PIN</label>
          <input
            className="input-base text-center text-2xl tracking-widest"
            type="password"
            inputMode="numeric"
            placeholder="••••"
            value={pin}
            onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={e => e.key === 'Enter' && onUnlock()}
            autoFocus
            maxLength={6}
          />
          {error && (
            <p className="text-ab-fire text-xs mt-1 font-display font-bold text-center">{error}</p>
          )}
        </div>

        <button onClick={onUnlock} className="btn-primary w-full">
          Unlock →
        </button>
      </div>
    </div>
  )
}

// ── Step: Login (Simple login for existing users) ──
function LoginStep({
  form,
  errors,
  onChange,
  onLogin,
}: {
  form: LoginForm
  errors: Partial<LoginForm>
  onChange: (field: keyof LoginForm, val: string) => void
  onLogin: () => void
}) {
  return (
    <div className="card-gold p-8">
      <div className="text-center mb-8">
        <h2 className="font-display font-black text-xl text-ab-text tracking-tight mb-3">
          Welcome Back
        </h2>
        <div className="divider-gold" />
      </div>

      <div className="flex flex-col gap-5">
        <div>
          <label className="text-label block mb-2">Username</label>
          <input
            className="input-base"
            placeholder="your_handle"
            name="username"
            autoComplete="username"
            value={form.username}
            onChange={e => onChange('username', e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onLogin()}
            autoFocus
            maxLength={24}
          />
          {errors.username && (
            <p className="text-ab-fire text-xs mt-1 font-display font-bold">{errors.username}</p>
          )}
        </div>

        <div>
          <label className="text-label block mb-2">Email <span className="opacity-50">(optional)</span></label>
          <input
            className="input-base"
            type="email"
            placeholder="you@example.com (optional)"
            name="email"
            autoComplete="email"
            value={form.email}
            onChange={e => onChange('email', e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onLogin()}
          />
        </div>

        <div>
          <label className="text-label block mb-2">PIN <span className="opacity-70 font-normal">(4–6 digits)</span></label>
          <input
            className="input-base"
            type="text"
            placeholder="••••"
            name="pin"
            autoComplete="current-password"
            value={form.pin}
            onChange={e => onChange('pin', e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onLogin()}
            maxLength={6}
            style={{ 
              letterSpacing: '4px'
            }}
          />
          {errors.pin && (
            <p className="text-ab-fire text-xs mt-1 font-display font-bold">{errors.pin}</p>
          )}
        </div>

        <button onClick={onLogin} className="btn-primary w-full mt-2">
          Login →
        </button>
      </div>
    </div>
  )
}

// ── Step 2: Faction ──
function FactionStep({
  selected,
  onSelect,
  onBack,
  onComplete,
}: {
  selected: FactionId | null
  onSelect: (id: FactionId) => void
  onBack: () => void
  onComplete: () => void
}) {
  return (
    <div>
      <div className="text-center mb-6">
        <p className="text-label mb-1">Step 2 of 2</p>
        <h2 className="font-display font-black text-xl text-ab-text tracking-tight">
          Choose Your Faction
        </h2>
      </div>

      <div className="flex flex-col gap-3 mb-6">
        {FACTIONS.map(faction => (
          <button
            key={faction.id}
            onClick={() => onSelect(faction.id)}
            className={`
              w-full text-left p-4 rounded-ab border transition-all duration-200 cursor-pointer
              ${selected === faction.id
                ? 'bg-ab-surface2 border-opacity-60'
                : 'bg-ab-surface border-white border-opacity-5 hover:border-opacity-15'
              }
            `}
            style={selected === faction.id ? { borderColor: faction.color } : {}}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl leading-none mt-0.5">{faction.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span
                    className="font-display font-black text-sm tracking-tight"
                    style={{ color: selected === faction.id ? faction.color : '#EAF6FF' }}
                  >
                    {faction.name}
                  </span>
                  {selected === faction.id && (
                    <span className="text-xs font-display font-bold" style={{ color: faction.color }}>✓</span>
                  )}
                </div>
                <p className="font-display font-bold text-xs text-ab-text-dim tracking-wide italic mb-1">
                  "{faction.tagline}"
                </p>
                <p className="text-body text-xs leading-snug">
                  {faction.description}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="flex gap-3">
        <button onClick={onBack} className="btn-ghost px-4 py-3 border border-white border-opacity-10 rounded-ab">
          ← Back
        </button>
        <button
          onClick={onComplete}
          disabled={!selected}
          className={`btn-primary flex-1 ${!selected ? 'opacity-40 cursor-not-allowed' : ''}`}
        >
          Enter the Atlas →
        </button>
      </div>
    </div>
  )
}
